import axios from 'axios'

export default {
  namespaced: true,
  state: {
    items: [],
    selectedIpo: null,
    includedFee: 0,

    orderPayload: {
      size: null,
      rate: 0,
      order_type: 2,
      interest: 0,
    },
    orderList: [],
    hasActiveOrder: false,
    activeOrder: null,
    submitting: false,
    cancelling: false,
    orderLoading: false,
    listLoading: true,
    orderProgress: {},
  },

  getters: {
    items: (s) => s.items,
    selectedIpo: (s) => s.selectedIpo,
    includedFee: (s) => s.includedFee,
    orderPayload: (s) => s.orderPayload,
    orderList: (s) => s.orderList,
    hasActiveOrder: (s) => s.hasActiveOrder,
    activeOrder: (s) => s.activeOrder,
    submitting: (s) => s.submitting,
    cancelling: (s) => s.cancelling,
    orderLoading: (s) => s.orderLoading,
    listLoading: (s) => s.listLoading,
    orderProgress: (s) => s.orderProgress,
  },

  mutations: {
    SET_ITEMS(state, items) {
      state.items = items
    },
    SET_SELECTED_IPO(state, ipo) {
      state.selectedIpo = ipo
    },
    SET_INCLUDED_FEE(state, val) {
      state.includedFee = val
    },
    SET_ORDER_PAYLOAD(state, payload) {
      state.orderPayload = { ...state.orderPayload, ...payload }
    },
    RESET_SIZE(state) {
      state.orderPayload = { ...state.orderPayload, size: null, rate: 0, interest: 0 }
    },
    SET_ORDER_LIST(state, list) {
      state.orderList = list
    },
    SET_HAS_ACTIVE_ORDER(state, val) {
      state.hasActiveOrder = val
    },
    SET_ACTIVE_ORDER(state, order) {
      state.activeOrder = order
    },
    SET_SUBMITTING(state, val) {
      state.submitting = val
    },
    SET_CANCELLING(state, val) {
      state.cancelling = val
    },
    SET_ORDER_LOADING(state, val) {
      state.orderLoading = val
    },
    SET_LIST_LOADING(state, val) {
      state.listLoading = val
    },
    SET_ORDER_PROGRESS(state, val) {
      state.orderProgress = val
    },
    SET_ORDER_TYPE(state, orderType) {
      state.orderPayload = { ...state.orderPayload, order_type: orderType }
    },
  },

  actions: {
    async fetchIpoList({ commit, dispatch }) {
      try {
        commit('SET_LIST_LOADING', true)
        const items = (await axios.get('/api/v2/user/ipo-list')).data
        commit('SET_ITEMS', items)
        if (items.length > 0) {
          commit('SET_SELECTED_IPO', items[0])
          dispatch('getOrders')
          dispatch('fetchProgress')
        }
      } catch (e) {
        console.error('fetchIpoList error:', e)
      } finally {
        commit('SET_LIST_LOADING', false)
      }
    },

    selectIpo({ commit, dispatch, state }, ipo) {
      commit('SET_SELECTED_IPO', ipo)
      const orderType = _getOrderTypeForIpo(ipo, state.orderPayload.order_type)
      commit('SET_ORDER_TYPE', orderType)
      dispatch('getOrders', orderType)
    },

    selectOrderType({ commit, dispatch }, orderType) {
      commit('SET_ORDER_TYPE', orderType)
      dispatch('getOrders', orderType)
    },

    async getOrders({ commit, state }, orderType) {
      const ipo = state.selectedIpo
      if (!ipo) return
      const type = orderType ?? state.orderPayload.order_type
      try {
        commit('SET_ORDER_LOADING', true)
        const list = (await axios.get(`/api/v2/user/orders/${ipo.symbol}/${type}`)).data
        commit('SET_ORDER_LIST', list)
        commit('SET_ACTIVE_ORDER', null)
        commit('SET_HAS_ACTIVE_ORDER', false)
        _findActiveOrder(commit, list)
      } catch (e) {
        console.error('getOrders error:', e)
      } finally {
        commit('SET_ORDER_LOADING', false)
      }
    },

    async sendOrder({ commit, state, dispatch }) {
      const ipo = state.selectedIpo
      if (!ipo) return
      try {
        commit('SET_SUBMITTING', true)
        await axios.post('/api/v2/user/order', { ...state.orderPayload, ipo_id: ipo.id })
        commit('RESET_SIZE')
        dispatch('getOrders', state.orderPayload.order_type)
      } catch (e) {
        console.error('sendOrder error:', e)
      } finally {
        commit('SET_SUBMITTING', false)
      }
    },

    async cancelOrder({ commit, state, dispatch }) {
      const orderId = state.activeOrder?.id
      if (!orderId) return
      try {
        commit('SET_CANCELLING', true)
        await axios.delete(`/api/v2/user/order/${orderId}`)
        dispatch('getOrders', state.orderPayload.order_type)
      } catch (e) {
        console.error('cancelOrder error:', e)
      } finally {
        commit('SET_CANCELLING', false)
      }
    },

    updateIncludedFee({ commit }, val) {
      commit('SET_INCLUDED_FEE', val)
    },

    updateOrderPayload({ commit }, payload) {
      commit('SET_ORDER_PAYLOAD', payload)
    },

    // One request returns progress for every active IPO. Reduce it to a map
    // keyed by symbol, since IpoProgress.vue reads orderProgress[symbol].
    async fetchProgress({ commit }) {
      try {
        const list = (await axios.get('/api/v2/user/progress')).data
        // When the MSE progress host is unreachable the backend returns
        // {progress: 0} instead of an array.
        if (!Array.isArray(list)) return
        const map = {}
        for (const item of list) {
          map[item.symbol] = item.progress
        }
        commit('SET_ORDER_PROGRESS', map)
      } catch (e) {
        console.error('fetchProgress error:', e)
      }
    },
  },
}

function _getOrderTypeForIpo(ipo, currentType) {
  switch (String(ipo.sell_type)) {
    case '4': return 1
    case '5': return 2
    default: return currentType
  }
}

function _findActiveOrder(commit, list) {
  const active = list.find(
    (o) => o.is_active && (o.state === 3000 || o.state === 1000),
  )
  if (active) {
    commit('SET_ACTIVE_ORDER', active)
    commit('SET_HAS_ACTIVE_ORDER', true)
    commit('SET_ORDER_PAYLOAD', { size: active.size, rate: active.rate, id: active.id })
  } else {
    commit('SET_HAS_ACTIVE_ORDER', false)
    commit('SET_ACTIVE_ORDER', null)
  }
}



// _____________________________________________________________________________________________
// import axios from 'axios'

// const USE_MOCK = true

// const MOCK_ITEMS = [
//   {
//     id: 1,
//     symbol: 'INVC',
//     type: 1,
//     sell_type: '1',
//     exchange_server_code: 'MSE',
//     currency_code: 'MNT',
//     pre_sale: false,
//     gather_source: 'NOMINAL',
//     auction_time: null,
//     brand_bond: null,
//     detail: {
//       unit_price: 1500,
//       collection_amount: 1500000000,
//       suggested_share: 1000000,
//       suggested_percent: 30,
//     },
//   },
//   {
//     id: 2,
//     symbol: 'BODI',
//     type: 1,
//     sell_type: '2',
//     exchange_server_code: 'MSE',
//     currency_code: 'MNT',
//     pre_sale: false,
//     gather_source: 'MCSD',
//     auction_time: null,
//     brand_bond: null,
//     detail: {
//       unit_price: 4200,
//       collection_amount: 840000000,
//       suggested_share: 200000,
//       suggested_percent: 20,
//     },
//   },
//   {
//     id: 3,
//     symbol: 'GBN-28',
//     type: 3,
//     sell_type: '3',
//     exchange_server_code: 'MSE',
//     currency_code: 'MNT',
//     pre_sale: false,
//     gather_source: 'NOMINAL',
//     auction_time: '2025-06-20 10:00',
//     brand_bond: { symbol_name: 'Засгийн газрын бонд 2028', frequency: '2' },
//     detail: {
//       unit_price: 1000,
//       collection_amount: 2000000000,
//       suggested_share: 2000000,
//       suggested_percent: null,
//       min_interest: 8.5,
//       max_interest: 12.0,
//     },
//   },
// ]

// const MOCK_ORDERS = [
//   {
//     id: 101,
//     size: 200,
//     rate: 1500,
//     amount: 300000,
//     fee_amount: 1680,
//     state: 3000,
//     is_active: false,
//     interest: null,
//     type: 2,
//     mse_message: null,
//     created_at: '2025-06-01T10:23:00',
//   },
//   {
//     id: 102,
//     size: 500,
//     rate: 1500,
//     amount: 750000,
//     fee_amount: 4200,
//     state: 4000,
//     is_active: false,
//     interest: null,
//     type: 2,
//     mse_message: 'Биелсэн',
//     created_at: '2025-06-02T14:10:00',
//   },
//   {
//     id: 103,
//     size: 100,
//     rate: 1500,
//     amount: 150000,
//     fee_amount: 840,
//     state: 1000,
//     is_active: true,
//     interest: null,
//     type: 2,
//     mse_message: null,
//     created_at: '2025-06-05T09:45:00',
//   },
// ]

// const MOCK_PROGRESS = { INVC: 28.4, BODI: 32.1 }

// export default {
//   namespaced: true,
//   state: {
//     items: [],
//     selectedIpo: null,
//     includedFee: 0,

//     orderPayload: {
//       size: null,
//       rate: 0,
//       order_type: 2,
//       interest: 0,
//     },
//     orderList: [],
//     hasActiveOrder: false,
//     activeOrder: null,
//     submitting: false,
//     cancelling: false,
//     orderLoading: false,
//     orderProgress: {},
//   },

//   getters: {
//     items: (s) => s.items,
//     selectedIpo: (s) => s.selectedIpo,
//     includedFee: (s) => s.includedFee,
//     orderPayload: (s) => s.orderPayload,
//     orderList: (s) => s.orderList,
//     hasActiveOrder: (s) => s.hasActiveOrder,
//     activeOrder: (s) => s.activeOrder,
//     submitting: (s) => s.submitting,
//     cancelling: (s) => s.cancelling,
//     orderLoading: (s) => s.orderLoading,
//     orderProgress: (s) => s.orderProgress,
//   },

//   mutations: {
//     SET_ITEMS(state, items) {
//       state.items = items
//     },
//     SET_SELECTED_IPO(state, ipo) {
//       state.selectedIpo = ipo
//     },
//     SET_INCLUDED_FEE(state, val) {
//       state.includedFee = val
//     },
//     SET_ORDER_PAYLOAD(state, payload) {
//       state.orderPayload = { ...state.orderPayload, ...payload }
//     },
//     RESET_SIZE(state) {
//       state.orderPayload = { ...state.orderPayload, size: null, rate: 0, interest: 0 }
//     },
//     SET_ORDER_LIST(state, list) {
//       state.orderList = list
//     },
//     SET_HAS_ACTIVE_ORDER(state, val) {
//       state.hasActiveOrder = val
//     },
//     SET_ACTIVE_ORDER(state, order) {
//       state.activeOrder = order
//     },
//     SET_SUBMITTING(state, val) {
//       state.submitting = val
//     },
//     SET_CANCELLING(state, val) {
//       state.cancelling = val
//     },
//     SET_ORDER_LOADING(state, val) {
//       state.orderLoading = val
//     },
//     SET_ORDER_PROGRESS(state, val) {
//       state.orderProgress = val
//     },
//     SET_ORDER_TYPE(state, orderType) {
//       state.orderPayload = { ...state.orderPayload, order_type: orderType }
//     },
//   },

//   actions: {
//     async fetchIpoList({ commit, dispatch }) {
//       try {
//         const items = USE_MOCK
//           ? MOCK_ITEMS
//           : (await axios.get('/v2/user/ipo-list')).data
//         commit('SET_ITEMS', items)
//         if (items.length > 0) {
//           commit('SET_SELECTED_IPO', items[0])
//           dispatch('getOrders')
//           dispatch('fetchProgress')
//         }
//       } catch (e) {
//         console.error('fetchIpoList error:', e)
//       }
//     },

//     selectIpo({ commit, dispatch, state }, ipo) {
//       commit('SET_SELECTED_IPO', ipo)
//       const orderType = _getOrderTypeForIpo(ipo, state.orderPayload.order_type)
//       commit('SET_ORDER_TYPE', orderType)
//       dispatch('getOrders', orderType)
//     },

//     selectOrderType({ commit, dispatch }, orderType) {
//       commit('SET_ORDER_TYPE', orderType)
//       dispatch('getOrders', orderType)
//     },

//     async getOrders({ commit, state }, orderType) {
//       const ipo = state.selectedIpo
//       if (!ipo) return
//       const type = orderType ?? state.orderPayload.order_type
//       try {
//         commit('SET_ORDER_LOADING', true)
//         const list = USE_MOCK
//           ? MOCK_ORDERS
//           : (await axios.get(`/api/v2/user/orders/${ipo.symbol}/${type}`)).data
//         commit('SET_ORDER_LIST', list)
//         commit('SET_ACTIVE_ORDER', null)
//         commit('SET_HAS_ACTIVE_ORDER', false)
//         _findActiveOrder(commit, list)
//       } catch (e) {
//         console.error('getOrders error:', e)
//       } finally {
//         commit('SET_ORDER_LOADING', false)
//       }
//     },

//     async sendOrder({ commit, state, dispatch }) {
//       const ipo = state.selectedIpo
//       if (!ipo) return
//       try {
//         commit('SET_SUBMITTING', true)
//         if (!USE_MOCK) {
//           await axios.post('/api/v2/user/order', { ...state.orderPayload, ipo_id: ipo.id })
//         }
//         commit('RESET_SIZE')
//         dispatch('getOrders', state.orderPayload.order_type)
//       } catch (e) {
//         console.error('sendOrder error:', e)
//       } finally {
//         commit('SET_SUBMITTING', false)
//       }
//     },

//     async cancelOrder({ commit, state, dispatch }) {
//       const orderId = state.orderPayload.id
//       if (!orderId) return
//       try {
//         commit('SET_CANCELLING', true)
//         if (!USE_MOCK) {
//           await axios.delete(`/api/v2/user/order/${orderId}`)
//         }
//         dispatch('getOrders', state.orderPayload.order_type)
//       } catch (e) {
//         console.error('cancelOrder error:', e)
//       } finally {
//         commit('SET_CANCELLING', false)
//       }
//     },

//     updateIncludedFee({ commit }, val) {
//       commit('SET_INCLUDED_FEE', val)
//     },

//     updateOrderPayload({ commit }, payload) {
//       commit('SET_ORDER_PAYLOAD', payload)
//     },

//     async fetchProgress({ commit, state }) {
//       const ipo = state.selectedIpo
//       if (!ipo) return
//       try {
//         const progress = USE_MOCK
//           ? MOCK_PROGRESS
//           : (await axios.get(`/api/v2/user/ipo-progress/${ipo.symbol}`)).data
//         commit('SET_ORDER_PROGRESS', progress)
//       } catch (e) {
//         console.error('fetchProgress error:', e)
//       }
//     },
//   },
// }

// function _getOrderTypeForIpo(ipo, currentType) {
//   switch (String(ipo.sell_type)) {
//     case '4': return 1
//     case '5': return 2
//     default: return currentType
//   }
// }

// function _findActiveOrder(commit, list) {
//   const active = list.find(
//     (o) => o.is_active && (o.state === 3000 || o.state === 1000),
//   )
//   if (active) {
//     commit('SET_ACTIVE_ORDER', active)
//     commit('SET_HAS_ACTIVE_ORDER', true)
//     commit('SET_ORDER_PAYLOAD', { size: active.size, rate: active.rate, id: active.id })
//   } else {
//     commit('SET_HAS_ACTIVE_ORDER', false)
//     commit('SET_ACTIVE_ORDER', null)
//   }
// }
